import React, { useEffect, useState } from "react";
import { Card, Col, message, Modal, Row, Switch, Tabs, Typography } from "antd";
import { ExclamationCircleOutlined, SafetyOutlined, ToolOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { multiFactor, TotpMultiFactorGenerator } from "firebase/auth";
import { deleteField, doc, updateDoc } from "firebase/firestore";
import { auth, firestore as db } from "configs/FirebaseConfig";
import TotpSetup from "components/layout-components/TotpSetup";
import ReauthenticationModal from "components/layout-components/ReauthenticationModal";
import { useAuth } from "../../../data/hooks/firebase/useAuth";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Profile = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("account");

  // Reauthentication state
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [pendingOperation, setPendingOperation] = useState(null);

  // Barcode scanner FAB setting
  const [barcodeScannerEnabled, setBarcodeScannerEnabled] = useState(() => {
    const saved = localStorage.getItem("barcodeScannerEnabled");
    return saved === "true";
  });

  // On-screen keyboard setting
  const [onScreenKeyboardEnabled, setOnScreenKeyboardEnabled] = useState(() => {
    const saved = localStorage.getItem("onScreenKeyboardEnabled");
    return saved !== "false"; // Default to true
  });

  const { user: authUser, profile, role } = useAuth();
  const isSuperAdmin = role === "superAdmin";
  const totpEnabled = profile?.totpEnabled || false;

  // Switch to tools tab if URL hash is #tools
  useEffect(() => {
    if (location.hash === "#tools") {
      setActiveTab("tools");
    }
  }, [location.hash]);

  // Handle reauthentication success
  const handleReauthSuccess = async () => {
    setShowReauthModal(false);

    if (pendingOperation === "disable") {
      await performDisableTotp();
    }

    setPendingOperation(null);
  };

  // Handle reauthentication cancel
  const handleReauthCancel = () => {
    setShowReauthModal(false);
    setPendingOperation(null);
  };

  // Perform the actual TOTP disable operation (after reauthentication)
  const performDisableTotp = async () => {
    const user = auth.currentUser;

    if (!user) {
      throw new Error("No authenticated user");
    }

    const enrolledFactors = multiFactor(user).enrolledFactors;

    // Find and unenroll TOTP factor
    const totpFactor = enrolledFactors.find((factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID);

    if (totpFactor) {
      await multiFactor(user).unenroll(totpFactor);
    }

    // Update Firestore using the v9 API
    await updateDoc(doc(db, "users", authUser.uid), {
      totpEnabled: false,
      totpSetupDate: deleteField(),
    });

    message.success(t("notifications.totp_disabled"));
  };

  // Disable TOTP (with reauthentication check)
  const disableTotp = async () => {
    try {
      setLoading(true);
      await performDisableTotp();
    } catch (error) {
      console.error("Error disabling TOTP:", error);

      // Check if reauthentication is required
      if (error.code === "auth/requires-recent-login") {
        setPendingOperation("disable");
        setShowReauthModal(true);
      } else {
        message.error(t("notifications.totp_disable_error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTotpToggle = async (enabled) => {
    if (!enabled && totpEnabled) {
      // Show confirmation modal before disabling TOTP
      Modal.confirm({
        title: t("disable_totp_confirmation_title"),
        content: t("disable_totp_confirmation_message"),
        icon: <ExclamationCircleOutlined />,
        okText: t("yes_disable"),
        cancelText: t("cancel"),
        okType: "danger",
        onOk: async () => {
          await disableTotp();
        },
      });
    }
  };

  const handleBarcodeScannerToggle = (enabled) => {
    setBarcodeScannerEnabled(enabled);
    localStorage.setItem("barcodeScannerEnabled", enabled.toString());
    message.success(enabled ? t("notifications.barcode_scanner_enabled") : t("notifications.barcode_scanner_disabled"));
  };

  const handleOnScreenKeyboardToggle = (enabled) => {
    setOnScreenKeyboardEnabled(enabled);
    localStorage.setItem("onScreenKeyboardEnabled", enabled.toString());
    message.success(
      enabled ? t("notifications.on_screen_keyboard_enabled") : t("notifications.on_screen_keyboard_disabled"),
    );
  };

  return (
    <div>
      {/* Reauthentication Modal */}
      <ReauthenticationModal
        visible={showReauthModal}
        onSuccess={handleReauthSuccess}
        onCancel={handleReauthCancel}
        operationName={t("disable_totp")}
      />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane
                tab={
                  <span>
                    <UserOutlined /> {t("account")}
                  </span>
                }
                key="account"
              >
                <div style={{ padding: "20px 0" }}>
                  <div style={{ marginBottom: "16px" }}>
                    <Text strong>{t("email")}: </Text>
                    <Text>{authUser.email}</Text>
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <Text strong>{t("role")}: </Text>
                    <Text>{role}</Text>
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <Text strong>{t("name")}: </Text>
                    <Text>
                      {profile?.userFirstName} {profile?.userLastName}
                    </Text>
                  </div>
                </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <SafetyOutlined /> {t("security")}
                  </span>
                }
                key="security"
              >
                <div style={{ padding: "20px 0" }}>
                  <Title level={4}>{t("two_factor_authentication")}</Title>

                  <div style={{ marginBottom: "24px" }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                      <Text strong style={{ marginRight: "12px" }}>
                        {t("totp_enabled")}:{" "}
                      </Text>
                      <Switch
                        checked={totpEnabled}
                        onChange={handleTotpToggle}
                        disabled={loading || (isSuperAdmin && !totpEnabled)}
                      />
                    </div>
                    <Text type="secondary">
                      {totpEnabled ? t("totp_enabled_description") : t("totp_disabled_description")}
                    </Text>
                  </div>

                  <TotpSetup
                    isEnabled={totpEnabled}
                    isRequired={isSuperAdmin}
                    onSetupComplete={() => {
                      // Refresh user profile or update state
                      window.location.reload();
                    }}
                  />
                </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <ToolOutlined /> {t("tools")}
                  </span>
                }
                key="tools"
              >
                <div style={{ padding: "20px 0" }}>
                  <Title level={4}>{t("barcode_scanner")}</Title>

                  <div style={{ marginBottom: "24px" }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                      <Text strong style={{ marginRight: "12px" }}>
                        {t("barcode_scanner_fab_enabled")}:{" "}
                      </Text>
                      <Switch checked={barcodeScannerEnabled} onChange={handleBarcodeScannerToggle} />
                    </div>
                    <Text type="secondary">{t("barcode_scanner_fab_description")}</Text>
                  </div>

                  <Title level={4}>{t("on_screen_keyboard")}</Title>

                  <div style={{ marginBottom: "24px" }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                      <Text strong style={{ marginRight: "12px" }}>
                        {t("on_screen_keyboard_enabled")}:{" "}
                      </Text>
                      <Switch checked={onScreenKeyboardEnabled} onChange={handleOnScreenKeyboardToggle} />
                    </div>
                    <Text type="secondary">{t("on_screen_keyboard_description")}</Text>
                  </div>
                </div>
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
